import indexOfObjectInArray from 'helper/indexOfObjectInArray';
import { ProgramItemType } from 'store/programItems';
import { actionTypes as programPreviewAT } from 'store/programPreview';
import { DateType } from 'types/Date';
import { OptionalProgramItemsEnum, OptionalWorkoutEnum } from 'types/services/magnolia/enums/OptionalProgramItemsEnum';
import { ProgramDaysActionType } from './programDays.actionCreators';
import {
	FLUSH_PROGRAM_DAYS,
	REQUEST_CALENDAR_PROGRAM_DAY_TODOS_SUCCESS,
	REQUEST_CALENDAR_SUCCESS,
	REQUEST_CALENDAR_TODAY_SUCCESS,
	REQUEST_DAY_SUCCESS,
	REQUEST_MOVE_ITEM,
	REQUEST_TODAY_SUCCESS,
} from './programDays.actionTypes';

export type ProgramDayType = {
	id: number;
	externalId: number;
	programId: string;
	dayNumber: number;
	itemsCheckable: boolean;
	date: DateType;
	programItems: number[];
	optionalWorkout: OptionalWorkoutEnum;
	optionalItems: OptionalProgramItemsEnum[];
	today: boolean;
	inFuture: boolean;
	lastDayOfProgram: boolean;
	hasActiveProgram: boolean;
	openItems: number;
	totalItems: number;
};

export type NormalizedProgramDayType = {
	key: DateType['dayOfMonth'];
	weekdayShort: string;
	weekday: string;
	date: string;
	isToday: ProgramDayType['today'];
	isLastDayOfProgram: ProgramDayType['lastDayOfProgram'];
	hasActiveProgram: ProgramDayType['hasActiveProgram'];
	itemsCheckable: ProgramDayType['itemsCheckable'];
	openItems: ProgramDayType['openItems'];
	optionalItems: Array<OptionalProgramItemsEnum>;
	totalItems: ProgramDayType['totalItems'];
	inFuture: ProgramDayType['inFuture'];
	dayNumber: ProgramDayType['dayNumber'];
};

export type ProgramDayMinType = {
	dayNumber: number;
	openItems: number;
};

export type ProgramDayActionType = {
	id: number;
	externalId: number;
	programId: string;
	dayNumber: number;
	itemsCheckable: boolean;
	date: DateType;
	programItems: Array<ProgramItemType>;
	optionalItems: Array<OptionalProgramItemsEnum>;
	today: boolean;
	inFuture: boolean;
	lastDayOfProgram: boolean;
	hasActiveProgram: boolean;
	openItems: number;
	totalItems: number;
};

export type ProgramDaysStateType = {
	readonly currentDayNumber?: number;
	readonly calendar: Array<ProgramDayType>;
	readonly calendarToday?: ProgramDayMinType;
	readonly allIds: Array<string>;
	readonly byId: { [key: string]: ProgramDayType } | null;
};

export type RootStateType = {
	programDays: ProgramDaysStateType;
};

// Reducer
// ------------------------------------

export const initialState: ProgramDaysStateType = {
	byId: null,
	allIds: [],
	calendar: [],
};

const appendTodosToCalendar = (calendar: ProgramDayType[], programDayTodos: ProgramDayMinType) => {
	const calendarIndex: number = indexOfObjectInArray(calendar as any, 'dayNumber', programDayTodos.dayNumber);
	if (calendarIndex < 0) {
		return;
	}
	calendar[calendarIndex] = {
		...calendar[calendarIndex],
		...programDayTodos,
	};
	return;
};

export default (state: ProgramDaysStateType = initialState, action: ProgramDaysActionType): ProgramDaysStateType => {
	switch (action.type) {
		case FLUSH_PROGRAM_DAYS:
		case programPreviewAT.START_SUCCESS as any:
		case programPreviewAT.OVERWRITE_SUCCESS as any:
			return { ...initialState };

		case REQUEST_CALENDAR_TODAY_SUCCESS:
			return {
				...state,
				calendarToday: action.calendarToday as any,
			};

		case REQUEST_CALENDAR_PROGRAM_DAY_TODOS_SUCCESS:
			const calendar = [...state.calendar];
			action.programDays.forEach(appendTodosToCalendar.bind(null, calendar));
			return {
				...state,
				calendar,
			};

		case REQUEST_CALENDAR_SUCCESS:
			return {
				...state,
				calendar: action.calendar,
			};

		case REQUEST_TODAY_SUCCESS:
			const optionalTodayItems: string[] = [];

			if ((action.day as any).optionalWorkout !== 'NONE') {
				optionalTodayItems.push('ITOPTWORKOUT');
			}

			return {
				...state,
				currentDayNumber: action.day.dayNumber,
				allIds: [...state.allIds, action.day.id.toString()],
				byId: {
					...(state.byId || {}),
					[action.day.id]: {
						id: action.day.id,
						optionalItems: optionalTodayItems,
						externalId: action.day.externalId,
						programId: action.day.programId,
						dayNumber: action.day.dayNumber,
						itemsCheckable: action.day.itemsCheckable,
						date: action.day.date,
						programItems: action.day.programItems.map((item) => item.id),
						today: action.day.today,
						inFuture: action.day.inFuture,
					},
				},
			};

		case REQUEST_DAY_SUCCESS:
			const optionalDayItems: string[] = [];

			if ((action.day as any).optionalWorkout !== 'NONE') {
				optionalDayItems.push('ITOPTWORKOUT');
			}

			return {
				...state,
				currentDayNumber: action.day.dayNumber,
				allIds: [...state.allIds, action.day.id.toString()],
				byId: {
					...(state.byId || {}),
					[action.day.id]: {
						id: action.day.id,
						externalId: action.day.externalId,
						programId: action.day.programId,
						dayNumber: action.day.dayNumber,
						date: action.day.date,
						programItems: action.day.programItems.map((item) => item.id),
						optionalItems: optionalDayItems,
						itemsCheckable: action.day.itemsCheckable,
						today: action.day.today,
						inFuture: action.day.inFuture,
					},
				},
			};

		case REQUEST_MOVE_ITEM:
			const { programItemId, dayId } = action;
			const selectedDayCopy: ProgramDayType = (state.byId || {})[`${dayId}`];

			if (!Array.isArray(selectedDayCopy.programItems)) {
				return state;
			}

			const selectedProgramItemIndex = selectedDayCopy.programItems.indexOf(parseInt(programItemId as any, 10));

			if (selectedProgramItemIndex > -1) {
				selectedDayCopy.programItems.splice(selectedProgramItemIndex, 1);
			}

			return {
				...state,
				byId: {
					[dayId]: selectedDayCopy,
				},
			};

		default:
			return state;
	}
};
